
import { Integration } from './types';
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/**
 * Base class for integration services
 */
export abstract class BaseIntegrationService<T extends Integration> {
  protected tableName: string;
  protected logPrefix: string;

  constructor(tableName: string, logPrefix: string) {
    this.tableName = tableName;
    this.logPrefix = logPrefix;
  }

  /**
   * Fetch integration data for a specific AI staff
   */
  async fetchIntegration(aiStaffId: string): Promise<T | null> {
    try {
      logger.info({
        message: `${this.logPrefix}: Fetching integration`,
        context: { aiStaffId }
      });
      
      const { data, error } = await supabase
        .from(this.tableName as any)
        .select('*')
        .eq('ai_staff_id', aiStaffId)
        .maybeSingle();
      
      if (error) {
        logger.error({
          message: `${this.logPrefix}: Error fetching integration`,
          error,
          context: { aiStaffId }
        });
        return null;
      }
      
      // Safely cast data to T type only after converting to unknown first
      return data as unknown as T;
    } catch (error) {
      logger.error({
        message: `${this.logPrefix}: Exception fetching integration`,
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      return null;
    }
  }

  /**
   * Save integration data
   */
  async saveIntegration(integration: T): Promise<boolean> {
    try {
      logger.info({
        message: `${this.logPrefix}: Saving integration`,
        context: { aiStaffId: integration.ai_staff_id }
      });
      
      const { error } = await supabase
        .from(this.tableName as any)
        .upsert({
          ...integration,
          updated_at: new Date().toISOString()
        } as any);
      
      if (error) {
        logger.error({
          message: `${this.logPrefix}: Error saving integration`,
          error,
          context: { aiStaffId: integration.ai_staff_id }
        });
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error({
        message: `${this.logPrefix}: Exception saving integration`,
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId: integration.ai_staff_id }
      });
      return false;
    }
  }

  /**
   * Delete integration data
   */
  async deleteIntegration(aiStaffId: string): Promise<boolean> {
    try {
      logger.info({
        message: `${this.logPrefix}: Deleting integration`,
        context: { aiStaffId }
      });
      
      const { error } = await supabase
        .from(this.tableName as any)
        .delete()
        .eq('ai_staff_id', aiStaffId);
      
      if (error) {
        logger.error({
          message: `${this.logPrefix}: Error deleting integration`,
          error,
          context: { aiStaffId }
        });
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error({
        message: `${this.logPrefix}: Exception deleting integration`,
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      return false;
    }
  }
  
  /**
   * Update integration settings
   */
  async updateSettings(aiStaffId: string, settings: Partial<T>): Promise<boolean> {
    try {
      logger.info({
        message: `${this.logPrefix}: Updating settings`,
        context: { aiStaffId, settings }
      });
      
      const { error } = await supabase
        .from(this.tableName as any)
        .update({
          ...settings,
          updated_at: new Date().toISOString()
        } as any)
        .eq('ai_staff_id', aiStaffId);
      
      if (error) {
        logger.error({
          message: `${this.logPrefix}: Error updating settings`,
          error,
          context: { aiStaffId }
        });
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error({
        message: `${this.logPrefix}: Exception updating settings`,
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      return false;
    }
  }
}
